import type { TExportExperienceSummaryV2ResponsePayload } from '@cascade/api-client/ge-v2';
import type { RequestError } from '@cascade/core/errors';
import { useExperience, useSummaryExport } from '@cascade/react/experiences';
import { useI18n } from '@cascade/react/i18n';
import type { TMessageKey, TReasonKeys } from '@cascade/react/i18n';
import { useUser } from '@cascade/react/user';
import { useConsumedSearchParams } from '@cascade/react-dom/hooks';
import { downloadFile } from '@cascade/react-dom/utils';
import { Button, Card, Icon, Text } from '@cascade/ui-library';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSummaryActionStatus } from './ExperienceSummary.helpers.ts';
import classes from './ExperienceSummary.module.scss';
import { SummaryAction } from './summary-action/index.ts';
import type { TSummaryActionStatus } from './summary-action/index.ts';
import { Page } from '#components/page/index.ts';
import { getExperiencePath, HOME_PATH, SUMMARY_PARAM } from '#config/index.ts';

const summaryParams = [SUMMARY_PARAM];

const downloadLabels = {
  idle: 'experience.summary.download',
  pending: 'experience.summary.download.pending',
  done: 'experience.summary.download',
  failed: 'experience.summary.retry',
} satisfies Record<TSummaryActionStatus, TMessageKey>;

const emailLabels = {
  idle: 'experience.summary.email',
  pending: 'experience.summary.email.pending',
  done: 'experience.summary.email.sent',
  failed: 'experience.summary.retry',
} satisfies Record<TSummaryActionStatus, TMessageKey>;

const summaryReasonKeys = {
  session_not_found: 'experience.summary.sessionExpired',
  empty_session: 'experience.summary.empty',
  summary_email_failed: 'experience.summary.emailFailed',
  summary_not_found: 'experience.summary.notFound',
} satisfies TReasonKeys;

const downloadSummary = ({
  download_url: url,
  filename,
}: TExportExperienceSummaryV2ResponsePayload) =>
  downloadFile({ url, filename });

export const ExperienceSummary = () => {
  const { translate, translateError } = useI18n();
  const { experienceId, experience } = useExperience();
  const { user } = useUser();
  const {
    canExportSummary,
    summary,
    isExportingSummary,
    exportSummaryError,
    isEmailingSummary,
    emailSummaryError,
    hasEmailedSummary,
    exportSummary,
    emailSummary,
  } = useSummaryExport();

  const summaryId =
    useConsumedSearchParams(summaryParams).get(SUMMARY_PARAM) ?? undefined;

  const navigate = useNavigate();
  const headingRef = useRef<HTMLDivElement>(null);

  const isBusy = isExportingSummary || isEmailingSummary;

  const downloadStatus = getSummaryActionStatus({
    isPending: isExportingSummary,
    isDone: Boolean(summary),
    error: exportSummaryError,
  });

  const emailStatus = getSummaryActionStatus({
    isPending: isEmailingSummary,
    isDone: hasEmailedSummary,
    error: emailSummaryError,
  });

  const describeError = (error?: RequestError) =>
    error ? translateError(error, summaryReasonKeys) : undefined;

  const downloadError = describeError(exportSummaryError);
  const emailError = describeError(emailSummaryError);
  const savedMessage = translate(
    canExportSummary
      ? 'experience.summary.keepCopy'
      : 'experience.summary.saved',
  );

  const download = () => {
    if (summary) {
      downloadSummary(summary);

      return;
    }

    exportSummary({ summaryId })
      .then(downloadSummary)
      .catch(() => undefined);
  };

  const sendEmail = () => {
    emailSummary().catch(() => undefined);
  };

  const reviewAnswers = () => {
    const experiencePath = getExperiencePath(experienceId);

    navigate(experiencePath);
  };

  const navigateHome = () => navigate(HOME_PATH);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!summaryId || !canExportSummary) {
      return;
    }

    exportSummary({ summaryId })
      .then(downloadSummary)
      .catch(() => undefined);
  }, [summaryId, canExportSummary, exportSummary]);

  return (
    <Page>
      <Card
        className={classes['experience-summary']}
        padding='xl'
        borderRadius='xl'
      >
        <div className={classes.body}>
          <Icon type='circle-check' color='text-success' size='3xl' />
          <div ref={headingRef} className={classes.heading} tabIndex={-1}>
            <Text
              type='h3'
              weight='semibold'
              content={translate('experience.summary.title')}
              compact
            />
          </div>
          <Text
            type='subtitle'
            content={translate('experience.summary.completed', {
              title: experience?.title ?? '',
            })}
          />
          <Text type='body-2' color='text-60' content={savedMessage} />
          {canExportSummary ? (
            <div className={classes.exports}>
              <SummaryAction
                iconType='file-pdf'
                title={translate('experience.summary.download.title')}
                description={translate(
                  'experience.summary.download.description',
                )}
                status={downloadStatus}
                actionLabel={translate(downloadLabels[downloadStatus])}
                errorMessage={downloadError}
                isDisabled={isBusy}
                onAction={download}
              />
              <SummaryAction
                iconType='envelope'
                title={translate('experience.summary.email.title', {
                  email: user?.email ?? '',
                })}
                description={translate('experience.summary.email.description')}
                status={emailStatus}
                actionLabel={translate(emailLabels[emailStatus])}
                errorMessage={emailError}
                isDisabled={isBusy || hasEmailedSummary}
                onAction={sendEmail}
              />
            </div>
          ) : null}
          <div className={classes.actions}>
            <Button
              variant='secondary'
              iconType='arrow-left'
              label={translate('experience.summary.review')}
              disabled={isBusy}
              onClick={reviewAnswers}
            />
            <Button
              variant='secondary'
              iconType='house'
              label={translate('experience.home')}
              disabled={isBusy}
              onClick={navigateHome}
            />
          </div>
        </div>
      </Card>
    </Page>
  );
};
